import { useCallback, useRef, useState, useEffect } from 'react';

const useAutoSave = (
    story,
    {
        delay = 5, // seconds
        undoCount = 10,
        onSave = null,
        disabled = false,
        disableOnElementFocus = false,
    },
) => {
    const autoSaveTimer = useRef(null);
    const valueRef = useRef(null);
    const lastValueRef = useRef([]);
    const [value, setValue] = useState(story); // This one does not trigger timer
    const [valueChanged, setValueChanged] = useState(false);

    const clearTimer = useCallback(() => {
        if (autoSaveTimer.current !== null) {
            clearInterval(autoSaveTimer.current);
        }
        autoSaveTimer.current = null;
    }, []);

    const save = useCallback(() => {
        clearTimer();
        setValueChanged(false);
        if (onSave !== null) {
            return onSave(valueRef.current);
        }
        return null;
    }, [value, onSave]);

    const autoSave = useCallback(() => {
        const elementFocussed = document.activeElement !== document.body;
        const disabledByElementFocus = disableOnElementFocus && elementFocussed;
        if (disabled || disabledByElementFocus) {
            return;
        }
        save();
    }, [save, setValueChanged, disabled]);

    // Do not put this in dependency
    const startTimer = useCallback(() => {
        clearTimer();
        autoSaveTimer.current = setInterval(autoSave, delay * 1000);
    }, [clearTimer, autoSave]);

    const onChange = useCallback(
        (newValue) => {
            lastValueRef.current.push(valueRef.current);
            if (lastValueRef.current.length >= undoCount) {
                lastValueRef.current.shift();
            }
            setValue(newValue);
            setValueChanged(true);
            if (!disabled) {
                startTimer();
            }
        },
        [disabled, setValue, setValueChanged, undoCount],
    );

    const onUndo = useCallback(() => {
        if (lastValueRef.current.length > 0) {
            setValue(lastValueRef.current.pop());
            setValueChanged(true);
            if (!disabled) {
                startTimer();
            }
        }
    }, [disabled, setValue, setValueChanged]);

    useEffect(() => {
        // Always overwrite from the top
        setValue(story);
    }, [story, setValue]);

    valueRef.current = value;
    const canUndo = lastValueRef.current.length > 0;

    return {
        storyValue: value,
        setValue,
        onChange,
        valueChanged,
        onManualSave: save,
        onUndo,
        canUndo,
    };
};

export default useAutoSave;
